/**
 * ShareGameModal — modal for sharing a library game.
 *
 * Supports public and private sharing. For private shares, allows
 * searching and selecting players and teams as recipients.
 * After share: link copy step with access explanation.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { ShareType } from '../../types/library';
import { PersonAddIcon, CloseIcon, GroupIcon, TagIcon, LinkIcon } from '../Icons';
import { BACKEND_URL } from '../../config';
import { buildLibraryShareUrl } from '../../utils/libraryDeepLink';

const EXCLUDED_SHARE_TAGS = new Set(['à classer', 'partagée']);

interface Recipient {
  type: 'player' | 'team';
  id: string;
  label: string;
}

interface ShareGameModalProps {
  pseudo: string;
  gameId: number;
  gameTags: string[];
  gameNote?: string;
  onClose: () => void;
  onShared?: (sharedId: number) => void;
}

function linkAccessHint(shareType: ShareType, allowReshare: boolean): string {
  if (shareType === 'public') {
    return 'Partage public : toute personne avec ce lien peut ouvrir la partie.';
  }
  if (allowReshare) {
    return 'Partage privé : le lien ne fonctionne que pour les destinataires autorisés. Ils pourront aussi le copier.';
  }
  return 'Partage privé verrouillé : seuls les destinataires désignés accèdent via la bibliothèque ou le chat. Ils ne peuvent pas copier le lien.';
}

export default function ShareGameModal({
  pseudo,
  gameId,
  gameTags,
  gameNote,
  onClose,
  onShared,
}: ShareGameModalProps) {
  const [shareType, setShareType] = useState<ShareType>('public');
  const [isPoll, setIsPoll] = useState(false);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [allowReshare, setAllowReshare] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');

  const [shareResult, setShareResult] = useState<{
    sharedId: number;
    shareType: ShareType;
    allowReshare: boolean;
  } | null>(null);

  const [copied, setCopied] = useState(false);
  const linkInputRef = useRef<HTMLInputElement>(null);

  const [selectedTags, setSelectedTags] = useState<Set<string>>(
    () => new Set(gameTags.filter(t => !EXCLUDED_SHARE_TAGS.has(t))),
  );

  const [shareComment, setShareComment] = useState(gameNote ?? '');

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Recipient[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shareUrl = shareResult
    ? buildLibraryShareUrl(shareResult.sharedId, {
        section: shareResult.shareType === 'public' ? 'shared_public' : 'shared_private',
        mode: 'replay',
      })
    : '';

  useEffect(() => {
    if (!shareResult) return;
    linkInputRef.current?.focus();
    linkInputRef.current?.select();
  }, [shareResult]);

  useEffect(() => {
    if (shareType === 'public') {
      setAllowReshare(true);
    }
  }, [shareType]);

  useEffect(() => {
    if (shareType !== 'private' || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    if (searchTimeout.current !== null) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      const results: Recipient[] = [];

      try {
        const [playersRes, teamsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/players/search?q=${encodeURIComponent(searchQuery)}&limit=5&compet=true`),
          fetch(`${BACKEND_URL}/api/teams/search?q=${encodeURIComponent(searchQuery)}&limit=5`),
        ]);

        if (playersRes.ok) {
          const data = await playersRes.json();
          for (const p of data.players ?? []) {
            if (p.pseudo.toLowerCase() !== pseudo.toLowerCase()) {
              results.push({ type: 'player', id: p.pseudo, label: p.pseudoDisplay || p.pseudo });
            }
          }
        }

        if (teamsRes.ok) {
          const data = await teamsRes.json();
          for (const t of data.teams ?? []) {
            results.push({ type: 'team', id: String(t.id), label: t.nom || `Équipe ${t.id}` });
          }
        }
      } catch { /* ignore */ }

      const existingIds = new Set(recipients.map(r => `${r.type}:${r.id}`));
      setSearchResults(results.filter(r => !existingIds.has(`${r.type}:${r.id}`)));
      setSearching(false);
    }, 300);

    return () => { if (searchTimeout.current !== null) clearTimeout(searchTimeout.current); };
  }, [searchQuery, shareType, pseudo, recipients]);

  const addRecipient = useCallback((r: Recipient) => {
    setRecipients(prev => [...prev, r]);
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  const removeRecipient = useCallback((idx: number) => {
    setRecipients(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const canShare = shareType === 'public' || recipients.length > 0;

  const toggleShareTag = useCallback((tag: string) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const shareableTags = gameTags.filter(t => !EXCLUDED_SHARE_TAGS.has(t));

  const handleCopyLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      linkInputRef.current?.select();
    }
  }, [shareUrl]);

  const handleShare = useCallback(async () => {
    if (sharing || shareResult || !canShare) return;
    setError('');
    setSharing(true);

    const effectiveAllowReshare = shareType === 'public' ? true : allowReshare;

    try {
      const body: Record<string, unknown> = {
        share_type: shareType,
        is_poll: isPoll,
        comments_enabled: commentsEnabled,
        selected_tags: [...selectedTags],
        share_comment: shareComment.trim() || undefined,
        allow_reshare: effectiveAllowReshare,
      };

      if (shareType === 'private') {
        body.recipients = recipients.map(r => ({
          recipient_type: r.type,
          recipient_id: r.id,
        }));
      }

      const res = await fetch(
        `${BACKEND_URL}/api/library/games/${gameId}/share?pseudo=${encodeURIComponent(pseudo)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );

      if (res.ok) {
        const data = await res.json();
        setShareResult({
          sharedId: data.shared_id,
          shareType,
          allowReshare: effectiveAllowReshare,
        });
        onShared?.(data.shared_id);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Erreur lors du partage');
      }
    } catch {
      setError('Erreur réseau');
    }
    setSharing(false);
  }, [
    sharing,
    shareResult,
    canShare,
    gameId,
    pseudo,
    shareType,
    isPoll,
    commentsEnabled,
    recipients,
    selectedTags,
    shareComment,
    allowReshare,
    onShared,
  ]);

  return (
    <div className="library-modal-overlay" onClick={onClose}>
      <div className="library-modal library-share-link-modal" onClick={e => e.stopPropagation()}>
        <div className="library-modal-header">
          <h3>{shareResult ? 'Partage réussi' : 'Partager la partie'}</h3>
          <button type="button" className="library-modal-close" onClick={onClose}>
            <CloseIcon size={16} />
          </button>
        </div>

        {shareResult ? (
          <>
            <div className="library-modal-body">
              <p className="library-share-link-success">
                La partie a été partagée. {shareResult.shareType === 'private'
                  ? 'Les destinataires la retrouveront dans leur bibliothèque et dans le chat.'
                  : 'Elle est visible dans la bibliothèque publique.'}
              </p>
              <p className="library-share-link-hint">
                {linkAccessHint(shareResult.shareType, shareResult.allowReshare)}
              </p>
              <label className="library-modal-label" htmlFor="share-game-link-input">
                Lien direct
              </label>
              <div className="library-share-link-row">
                <input
                  id="share-game-link-input"
                  ref={linkInputRef}
                  className="library-share-link-input"
                  readOnly
                  value={shareUrl}
                  onFocus={e => e.target.select()}
                />
                <button
                  type="button"
                  className={`library-modal-btn primary library-share-link-copy${copied ? ' copied' : ''}`}
                  onClick={() => { void handleCopyLink(); }}
                >
                  <LinkIcon size={14} />
                  {copied ? 'Copié ✓' : 'Copier'}
                </button>
              </div>
            </div>
            <div className="library-modal-footer">
              <div className="library-modal-buttons">
                <button type="button" className="library-modal-btn primary" onClick={onClose}>
                  Terminer
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="library-modal-body">
              <div className="library-modal-section">
                <label className="library-modal-label">Visibilité</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    className={`library-section-btn ${shareType === 'public' ? 'active' : ''}`}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => setShareType('public')}
                  >
                    Publique
                  </button>
                  <button
                    type="button"
                    className={`library-section-btn ${shareType === 'private' ? 'active' : ''}`}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => setShareType('private')}
                  >
                    Privée
                  </button>
                </div>
                <p className="library-share-type-hint">
                  {shareType === 'public'
                    ? 'Visible par tous les joueurs compétition. Le lien peut être transmis librement.'
                    : 'Réservée aux destinataires choisis. Accès via bibliothèque ou message chat.'}
                </p>
              </div>

              {shareType === 'private' && (
                <div className="library-modal-section">
                  <label className="library-modal-label">Destinataires</label>

                  {recipients.length > 0 && (
                    <div className="library-recipient-chips">
                      {recipients.map((r, i) => (
                        <span key={`${r.type}:${r.id}`} className="library-recipient-chip">
                          {r.type === 'team' && <GroupIcon size={10} />}
                          {r.label}
                          <button type="button" onClick={() => removeRecipient(i)}>×</button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="library-recipient-search">
                    <input
                      placeholder="Rechercher un joueur ou une équipe…"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                    {(searchResults.length > 0 || searching) && (
                      <div className="library-recipient-results">
                        {searching && (
                          <div className="library-recipient-item" style={{ color: 'var(--text-muted)', cursor: 'default' }}>
                            Recherche…
                          </div>
                        )}
                        {searchResults.map(r => (
                          <div
                            key={`${r.type}:${r.id}`}
                            className="library-recipient-item"
                            onClick={() => addRecipient(r)}
                          >
                            {r.type === 'player'
                              ? <PersonAddIcon size={14} />
                              : <GroupIcon size={14} />}
                            <span>{r.label}</span>
                            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                              {r.type === 'player' ? 'Joueur' : 'Équipe'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {recipients.length === 0 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                      Ajoutez au moins un destinataire pour un partage privé.
                    </div>
                  )}
                </div>
              )}

              {shareType === 'private' && (
                <div className="library-modal-section">
                  <label className="library-share-option">
                    <input
                      type="checkbox"
                      checked={allowReshare}
                      onChange={e => setAllowReshare(e.target.checked)}
                    />
                    <span>Autoriser les destinataires à copier le lien</span>
                  </label>
                  <p className="library-share-option-hint">
                    {allowReshare
                      ? 'Les destinataires pourront copier un lien (réservé aux personnes déjà autorisées).'
                      : 'Par défaut : accès uniquement via bibliothèque et chat, sans copie de lien.'}
                  </p>
                </div>
              )}

              <div className="library-modal-section">
                <label className="library-share-option">
                  <input
                    type="checkbox"
                    checked={commentsEnabled}
                    onChange={e => setCommentsEnabled(e.target.checked)}
                  />
                  <span>Autoriser les commentaires</span>
                </label>
              </div>

              <div className="library-modal-section">
                <label className="library-share-option">
                  <input
                    type="checkbox"
                    checked={isPoll}
                    onChange={e => setIsPoll(e.target.checked)}
                  />
                  <span>Mode sondage (les joueurs votent pour une carte)</span>
                </label>
              </div>

              {shareableTags.length > 0 && (
                <div className="library-modal-section">
                  <label className="library-modal-label">
                    <TagIcon size={12} /> Tags à partager
                  </label>
                  <div className="library-modal-tags">
                    {shareableTags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        className={`library-tag-editor-chip ${selectedTags.has(tag) ? 'selected' : ''}`}
                        data-tag={tag}
                        onClick={() => toggleShareTag(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="library-modal-section">
                <label className="library-modal-label">Commentaire du partage</label>
                <textarea
                  className="library-modal-textarea"
                  rows={2}
                  value={shareComment}
                  onChange={e => setShareComment(e.target.value)}
                  placeholder="Commentaire visible par les destinataires…"
                />
              </div>

              <div className="library-modal-section library-share-privacy-note">
                Les données personnelles (pseudos des joueurs humains) seront supprimées de la copie partagée.
              </div>

              {error && (
                <div className="library-modal-error">{error}</div>
              )}
            </div>

            <div className="library-modal-footer">
              <div className="library-modal-buttons">
                <button type="button" className="library-modal-btn secondary" onClick={onClose}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="library-modal-btn primary"
                  onClick={() => { void handleShare(); }}
                  disabled={sharing || !canShare}
                >
                  {sharing ? 'Partage en cours…' : 'Partager'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
